/**
 * src/shell/auth/services/BiometricTheater.ts
 *
 * Sensory Biometric Theater Engine.
 * Combines zero-asset Web Audio micro-chimes, haptic feedback (`navigator.vibrate`),
 * and accessibility guards (prefers-reduced-motion).
 */

export class BiometricTheater {
  private audioCtx: AudioContext | null = null;
  private soundEnabled = true;
  private hapticsEnabled = true;

  constructor() {
    if (typeof window !== 'undefined') {
      const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      if (prefersReducedMotion) {
        this.hapticsEnabled = false;
      }
    }
  }

  private _getAudioContext(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!this.audioCtx) {
      const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioCtxClass) {
        try {
          this.audioCtx = new AudioCtxClass();
        } catch {}
      }
    }
    if (this.audioCtx && this.audioCtx.state === 'suspended') {
      this.audioCtx.resume().catch(() => {});
    }
    return this.audioCtx;
  }

  /**
   * Triggers haptic vibration pattern if supported.
   */
  public vibrate(pattern: number | number[]): void {
    if (!this.hapticsEnabled || typeof navigator === 'undefined' || !('vibrate' in navigator)) return;
    try {
      navigator.vibrate(pattern);
    } catch {}
  }

  /**
   * Plays a crisp ascending harmonic chime (880Hz -> 1320Hz) on biometric success.
   */
  public playSuccessCelebration(): void {
    this.vibrate([15, 30, 15]);

    if (!this.soundEnabled) return;
    const ctx = this._getAudioContext();
    if (!ctx) return;

    try {
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, now); // A5
      osc.frequency.exponentialRampToValueAtTime(1320, now + 0.12); // E6

      gain.gain.setValueAtTime(0.08, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + 0.25);
    } catch {}
  }

  /**
   * Plays a subtle low damped tone (220Hz -> 140Hz) on biometric rejection/error.
   */
  public playErrorFeedback(): void {
    this.vibrate([50, 50, 50]);

    if (!this.soundEnabled) return;
    const ctx = this._getAudioContext();
    if (!ctx) return;

    try {
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(220, now);
      osc.frequency.exponentialRampToValueAtTime(140, now + 0.15);

      gain.gain.setValueAtTime(0.1, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + 0.2);
    } catch {}
  }

  public setSoundEnabled(enabled: boolean): void {
    this.soundEnabled = enabled;
  }

  public setHapticsEnabled(enabled: boolean): void {
    this.hapticsEnabled = enabled;
  }
}

export const biometricTheater = new BiometricTheater();
